import { useEffect, useState, useCallback } from 'react';
import { RootState, useAppSelector } from '../../../../state/state';
import { FormControl, MenuItem, TextField, Tooltip } from "@mui/material";
import { motion } from 'framer-motion';
import { OrderUi } from '../../types/OrderListUi';
import TodayTransactions from './TodayTransactions';
import { selectEffectiveUID } from '../../../../state/UserStatesV2/userSliceV2';
import { PieChart, Pie, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
interface CostProfile {
    due: number;
    collected: number;
    settled: number;
    platformFees: number;
    cycle: string;
    duePercentage: number;
    collectedPercentage: number;
}

export default function Statistics() {
    const [activeTab, setActiveTab] = useState<string>('overallStats');
    const [isLoading, setIsLoading] = useState(true);
    const [stats, setStats] = useState<CostProfile | null>(null);
    const [selectedCycle, setSelectedCycle] = useState<string>("All Cycles");
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string>("0");
    const orderState: OrderUi[] = useAppSelector((state: RootState) => state.OrderStatsSlice.data || []);
    const uid = useAppSelector(selectEffectiveUID);
    const AllSubscriptions = useAppSelector((state: RootState) => state.subscriptionTableSlice.data || []);

    // Get unique subscriptions
    const uniqueSubscriptions = AllSubscriptions.map((item) => ({
        id: item.id,
        name: item.Name
    }));
    const uniqueSubscriptionsWithAll = [{ name: "All Subscriptions", id: "0" }, ...uniqueSubscriptions];

    const loadStats = async () => {
        try {
            setIsLoading(true);

            // Filter orders based on selected subscription and cycle
            const filteredOrders = orderState.filter(order => {
                const subscriptionMatch = selectedSubscriptionId === "0" || order.subscriptionId === selectedSubscriptionId;
                const cycleMatch = selectedCycle === "All Cycles" || order.cycleName === selectedCycle;
                return subscriptionMatch && cycleMatch;
            });

            // Calculate statistics from filtered orders
            const totalPaidCharges = filteredOrders
                .filter(order => order.paymentstatus === 'Paid')
                .reduce((sum, order) => sum + (order.chargeable || 0), 0);

            const totalUnpaidCharges = filteredOrders
                .filter(order => order.paymentstatus !== 'Paid')
                .reduce((sum, order) => sum + (order.chargeable || 0), 0);

            const settledAmount = filteredOrders
                .filter(order => order.settlementStatus === 'Settled')
                .reduce((sum, order) => sum + (order.chargeable || 0), 0);

            const platformFees = filteredOrders
                .filter(order => order.settlementStatus === 'Settled')
                .reduce((sum, order) => sum + (order.platformFees || 0), 0);

            const totalAmount = totalPaidCharges + totalUnpaidCharges;
            const collectedPercentage = totalAmount ? Math.floor((totalPaidCharges / totalAmount) * 100) : 0;
            const duePercentage = totalAmount ? Math.floor((totalUnpaidCharges / totalAmount) * 100) : 0;

            setStats({
                due: totalUnpaidCharges,
                collected: totalPaidCharges,
                settled: settledAmount,
                platformFees: platformFees,
                cycle: selectedCycle,
                duePercentage,
                collectedPercentage
            });

            setIsLoading(false);
        } catch (error) {
            console.error('Error loading statistics:', error);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'overallStats') {
            loadStats();
        }
    }, [selectedCycle, selectedSubscriptionId, uid, activeTab]);

    const uniqueCycles = useCallback(() => Array.from(
        new Set(
            orderState
                .map(order => (order.cycleName !== undefined ? order.cycleName : null))
                .filter(cycleName => cycleName !== null)
        )
    ), [orderState]);

    useEffect(() => {
        if (uniqueCycles().length > 0) {
            setSelectedCycle(uniqueCycles()[0]);
        }
    }, [uniqueCycles]);
    const uniqueCyclesWithAll: string[] = ["All Cycles", ...uniqueCycles()];

    // Render the Overall Stats content
    const renderOverallStats = () => {
        if (isLoading) {
            return (
                <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
            );
        }

        return (

            <div>
                <div className="grid grid-cols-1 gap-4 mb-6">
                    <FormControl fullWidth>
                        <TextField
                            size="small"  
                            select
                            label="Select Subscription"
                            value={selectedSubscriptionId}
                            onChange={(e) => setSelectedSubscriptionId(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    fontSize: '14px',
                                }
                            }}
                        >
                            {uniqueSubscriptionsWithAll.map((item) => (
                                <MenuItem key={item.id} value={item.id}>
                                    {item.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </FormControl>

                    <FormControl fullWidth>
                        <TextField
                            size="small"
                            select
                            label="Select Cycle"
                            value={selectedCycle}
                            onChange={(e) => setSelectedCycle(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    fontSize: '14px',
                                }
                            }}
                        >
                            {uniqueCyclesWithAll.map((cycle, index) => (
                                <MenuItem key={index} value={cycle}>
                                    {cycle}
                                </MenuItem>
                            ))}
                        </TextField>
                    </FormControl>
                </div>

                {/* Main Financial Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Total Collected Card */}
                    <Tooltip
                        title="Amount collected from customers"
                        arrow
                        placement="top"
                    >
                        <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200 shadow-sm cursor-help">
                            <div className="flex items-start justify-between">
                                <div>
                                    <h3 className="text-base font-medium text-gray-900">Total Collected</h3>
                                    <p className="text-2xl font-bold text-green-600 mt-1">
                                        ₹{stats?.collected.toFixed(2)}
                                    </p>
                                    <p className="text-sm text-gray-600 mt-1">{stats?.collectedPercentage}% of total amount</p>
                                </div>
                                <motion.div
                                    className="p-2 bg-green-600 bg-opacity-10 rounded-full"
                                    animate={{
                                        scale: [1, 1.1, 1],
                                        rotate: [0, 5, -5, 0]
                                    }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        repeatType: "reverse"
                                    }}
                                >
                                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </motion.div>
                            </div>
                            <div className="mt-3">
                                <div className="h-1.5 bg-green-100 rounded-full overflow-hidden">
                                    <motion.div
                                        className="h-full bg-green-500 rounded-full"
                                        initial={{ width: 0 }}
                                        animate={{ width: `${stats?.collectedPercentage}%` }}
                                        transition={{ duration: 1 }}
                                    />
                                </div>
                            </div>
                        </div>
                    </Tooltip>

                    {/* Total Due Card */}
                    <Tooltip
                        title="Amount invoiced but not paid yet"
                        arrow
                        placement="top"
                    >
                        <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-xl border border-red-200 shadow-sm cursor-help">
                            <div className="flex items-start justify-between">
                                <div>
                                    <h3 className="text-base font-medium text-gray-900">Total Due</h3>
                                    <p className="text-2xl font-bold text-red-600 mt-1">
                                        ₹{stats?.due.toFixed(2)}
                                    </p>
                                    <p className="text-sm text-gray-600 mt-1">{stats?.duePercentage}% of total amount</p>
                                </div>
                                <motion.div
                                    className="p-2 bg-red-600 bg-opacity-10 rounded-full"
                                    animate={{
                                        scale: [1, 1.1, 1],
                                        rotate: [0, 5, -5, 0]
                                    }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        repeatType: "reverse"
                                    }}
                                >
                                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </motion.div>
                            </div>
                            <div className="mt-3">
                                <div className="h-1.5 bg-red-100 rounded-full overflow-hidden">
                                    <motion.div
                                        className="h-full bg-red-500 rounded-full"
                                        initial={{ width: 0 }}
                                        animate={{ width: `${stats?.duePercentage}%` }}
                                        transition={{ duration: 1 }}
                                    />
                                </div>
                            </div>
                        </div>
                    </Tooltip>

                    {/* Total Settled Card */}
                    <Tooltip
                        title="Amount transferred to all your accounts"
                        arrow
                        placement="top"
                    >
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200 shadow-sm cursor-help">
                            <div className="flex items-start justify-between">
                                <div>
                                    <h3 className="text-base font-medium text-gray-900">Total Settled</h3>
                                    <p className="text-2xl font-bold text-blue-600 mt-1">
                                        ₹{stats?.settled.toFixed(2)}
                                    </p>
                                    <p className="text-sm text-gray-600 mt-1">
                                        {((stats?.settled || 0) / (stats?.collected || 1) * 100).toFixed(1)}% of collected amount
                                    </p>
                                </div>
                                <motion.div
                                    className="p-2 bg-blue-600 bg-opacity-10 rounded-full"
                                    animate={{
                                        scale: [1, 1.1, 1],
                                        rotate: [0, 5, -5, 0]
                                    }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        repeatType: "reverse"
                                    }}
                                >
                                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </motion.div>
                            </div>
                            <div className="mt-3">
                                <div className="h-1.5 bg-blue-100 rounded-full overflow-hidden">
                                    <motion.div
                                        className="h-full bg-blue-500 rounded-full"
                                        initial={{ width: 0 }}
                                        animate={{ width: `${((stats?.settled || 0) / (stats?.collected || 1) * 100)}%` }}
                                        transition={{ duration: 1 }}
                                    />
                                </div>
                            </div>
                        </div>
                    </Tooltip>
                </div>

                {/* Pie Chart Visualization */}
                <div className="mt-6 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                    <h3 className="text-base font-semibold text-gray-900 mb-2">Financial Overview</h3>
                    <ResponsiveContainer width="100%" height={320}>
                        <PieChart>
                            <Pie
                                data={[
                                    {
                                        name: 'Collected',
                                        value: stats?.collected || 0,
                                        color: '#10b981'
                                    },
                                    {
                                        name: 'Due',
                                        value: stats?.due || 0,
                                        color: '#ef4444'
                                    },
                                    {
                                        name: 'Settled',
                                        value: stats?.settled || 0,
                                        color: '#3b82f6'
                                    }
                                ]}
                                cx="50%"
                                cy="50%"
                                labelLine={true}
                                label={({ name, value, percent }) =>
                                    `${name}: ₹${value.toFixed(0)} (${(percent * 100).toFixed(1)}%)`
                                }
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="value"
                                animationDuration={1000}
                            >
                                {[
                                    { name: 'Collected', value: stats?.collected || 0, color: '#10b981' },
                                    { name: 'Due', value: stats?.due || 0, color: '#ef4444' },
                                    { name: 'Settled', value: stats?.settled || 0, color: '#3b82f6' }
                                ].map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <RechartsTooltip
                                contentStyle={{
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                }}
                                formatter={(value: number) => `₹${value.toFixed(2)}`}
                            />
                            <Legend
                                wrapperStyle={{
                                    paddingTop: '12px'
                                }}
                                iconType="circle"
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Platform Fees (More Subtle Secondary Metric) */}
                <Tooltip
                    title="Fees paid to HoSaksham including payment gateway charges"
                    arrow
                    placement="top"
                >
                    <div className="mt-4 bg-gray-50 p-3 rounded-lg border border-gray-100 cursor-help hover:bg-gray-100 transition-colors duration-200">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-gray-100 rounded-full">
                                    <svg
                                        className="w-5 h-5 text-gray-500"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-600">Platform Fees</span>
                                    <div className="text-base font-semibold text-gray-800 mt-1">
                                        ₹{stats?.platformFees.toFixed(2)}
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                                <div className="w-32 h-1.5 bg-gray-100 rounded-full">
                                    <motion.div
                                        className="h-full bg-gray-400 rounded-full"
                                        initial={{ width: 0 }}
                                        animate={{
                                            width: `${((stats?.platformFees || 0) / (stats?.collected || 1) * 100)}%`
                                        }}
                                        transition={{ duration: 1 }}
                                    />
                                </div>
                                <span className="text-sm text-gray-500">
                                    {((stats?.platformFees || 0) / (stats?.collected || 1) * 100).toFixed(1)}% of collections
                                </span>
                            </div>
                        </div>
                    </div>
                </Tooltip>
            </div>

        );
    };

    // Render content based on active tab
    const renderContent = () => {
        switch (activeTab) {
            case 'todayTransactions':
                return <TodayTransactions />;
            case 'overallStats':
                return renderOverallStats();
            default:
                return <TodayTransactions />;
        }
    };

    return (
        <div className="p-6 bg-white rounded-xl shadow-sm">
            <div className="mb-4">
                <h1 className="text-2xl font-semibold text-gray-900">Financial Statistics</h1>
                
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-gray-200 mb-6">

                <button
                    onClick={() => setActiveTab('overallStats')}
                    className={`px-4 py-2 font-medium text-sm transition-colors duration-200 ${activeTab === 'overallStats'
                        ? 'text-cc_brand border-b-2 border-cc_brand'
                        : 'text-gray-500 hover:text-cc_brand_hover'
                        }`}
                >
                    Overall Stats
                </button>

                <button
                    onClick={() => setActiveTab('todayTransactions')}
                    className={`px-4 py-2 font-medium text-sm transition-colors duration-200 ${activeTab === 'todayTransactions'
                        ? 'text-cc_brand border-b-2 border-cc_brand'
                        : 'text-gray-500 hover:text-cc_brand_hover'
                        }`}
                >
                    Recent Transactions
                </button>

            </div>

            {/* Tab Content */}
            <div className="grid grid-cols-1 gap-6">
                {renderContent()}
            </div>
        </div>
    );
} 